/**
 * モードドリブン選択 UI (モードドリブン化リファクタ MD-3、2026-07-24)。
 *
 * mode の登記簿は Firestore `modeProfiles` (M10) に一本化されたため、各マスタ編集画面の
 * mode 選択肢はハードコード 4 種ではなく **登録済み ModeProfile から動的に生成**する。
 *
 * - options = 登録済み profile の mode キー (重複除去、表示は modeLabel ?? getModeLabel fallback)
 * - 現在値が未登録 mode でも「(未登録)」として必ず選択肢に含める — 旧 AiOutputFormatsPage の
 *   custom fallback パターン (2026-07-14) の一般化。legacy マスタが選択空欄に見える事故を防ぐ
 * - 読込中 / エラー時も現在値だけは表示し、値を勝手に変えない
 *
 * ModeSelectField (単一選択) と ModeMultiSelectField (複数選択 checkbox、M2 用) を提供。
 */

import {
  DISTRICT_MODES,
  getModeLabel,
  type DistrictMode,
  type ModeProfile,
} from '@citizen-walk/shared-types';
import { useModeProfiles } from '../useModeProfiles';

export interface ModeOption {
  value: string;
  label: string;
  /** ModeProfile (M10) に登録済みか。false は legacy データ由来の未登録キー。 */
  registered: boolean;
}

/**
 * 登録済み profile 一覧 + 現在値から選択肢を構築する (重複 mode キーは初出の profile を採用)。
 * 現在値が未登録なら末尾に「(未登録)」で追加し、既存データの表示・保存を壊さない。
 *
 * 【廃止予定 fallback】profile が 1 件も無い環境 (seed-mode-profiles.mjs 未適用の prod 等) では
 * 新規作成 select が空になり UI が使用不能になるため、組込4モード (DISTRICT_MODES、@deprecated)
 * を「(未登録)」扱いで補完する。seed 適用後は登録済み profile 側が使われこの補完は発動しない。
 * 撤去条件: 全環境の seed 適用完了。
 */
export function buildModeOptions(profiles: ModeProfile[], currentValues: string[]): ModeOption[] {
  const seen = new Set<string>();
  const options: ModeOption[] = [];
  for (const p of profiles) {
    const key = p.mode.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    options.push({ value: key, label: getModeLabel(key, p.modeLabel), registered: true });
  }
  if (options.length === 0) {
    for (const m of DISTRICT_MODES) {
      if (seen.has(m)) continue;
      seen.add(m);
      options.push({ value: m, label: `${getModeLabel(m)} (未登録)`, registered: false });
    }
  }
  for (const v of currentValues) {
    const key = v.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    options.push({ value: key, label: `${getModeLabel(key)} (未登録)`, registered: false });
  }
  return options;
}

/** useModeProfiles を options に変換する共通 hook。 */
export function useModeOptions(currentValues: string[]): {
  options: ModeOption[];
  loading: boolean;
} {
  const state = useModeProfiles();
  const profiles = state.status === 'ready' ? state.items : [];
  return {
    options: buildModeOptions(profiles, currentValues),
    loading: state.status === 'loading',
  };
}

const DEFAULT_SELECT_CLASS =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400';

export interface ModeSelectFieldProps {
  id?: string;
  value: string;
  onChange: (mode: DistrictMode) => void;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
  /**
   * 「指定なし」相当の空値選択肢を先頭に置く場合のラベル (M9 の targetMode='全 mode' 等)。
   * 未指定なら空値選択肢は出さない。
   */
  emptyOptionLabel?: string;
}

/** 単一 mode 選択 (M1/M3〜M9 のマスタ編集画面用、ModeProfile 駆動)。 */
export function ModeSelectField(props: ModeSelectFieldProps) {
  const { options, loading } = useModeOptions(props.value ? [props.value] : []);
  return (
    <select
      id={props.id}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value as DistrictMode)}
      className={props.className ?? DEFAULT_SELECT_CLASS}
      disabled={props.disabled}
      aria-label={props['aria-label']}
    >
      {props.emptyOptionLabel !== undefined && <option value="">{props.emptyOptionLabel}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
      {loading && options.length === 0 && props.value === '' && (
        <option value="" disabled>
          読込中…
        </option>
      )}
    </select>
  );
}

export interface ModeMultiSelectFieldProps {
  values: string[];
  onToggle: (mode: DistrictMode) => void;
  'aria-label'?: string;
}

/** 複数 mode 選択 checkbox 群 (M2 aiCategoryMasters の modes[] 用、ModeProfile 駆動)。 */
export function ModeMultiSelectField(props: ModeMultiSelectFieldProps) {
  const { options, loading } = useModeOptions(props.values);
  return (
    <div
      role="group"
      aria-label={props['aria-label'] ?? 'モード選択 (mode)'}
      className="flex flex-wrap gap-3 px-3 py-2 border border-slate-300 rounded-md bg-white"
    >
      {loading && options.length === 0 && <span className="text-xs text-slate-400">読込中…</span>}
      {options.map((o) => (
        <label key={o.value} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={props.values.includes(o.value)}
            onChange={() => props.onToggle(o.value)}
          />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}
